import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ListContainer from '../components/ListContainer';
import FormContainer from '../components/FormContainer';
import API_BASE_URL from '../config/api';

const InspectorSocietyListPage = ({ user }) => {
  const navigate = useNavigate();
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Server-side Pagination States
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [exporting, setExporting] = useState(false);

  // View mode: 'list' | 'create' | 'edit'
  const [viewMode, setViewMode] = useState('list');
  const [editingSociety, setEditingSociety] = useState(null);

  const [metaOptions, setMetaOptions] = useState({
    divisions: [],
    districts: [],
    arCircles: [],
    usersByRole: { dr: [], ar: [], inspector: [], secretary: [] }
  });

  const [formSaving, setFormSaving] = useState(false);
  const [formData, setFormData] = useState({
    SOCIETY: '',
    DIVISION: '',
    DISTRICT: '',
    AR: '',
    VILLAGE: '',
    selectedDR: '',
    selectedAR: '',
    selectedInspector: '',
    selectedSecretary: ''
  });

  // Fetch societies with server-side pagination & search
  const fetchSocieties = useCallback(async (targetPage = page, targetLimit = limit, targetSearch = searchTerm) => {
    setLoading(true);
    try {
      const role = (user?.role || '').toLowerCase();
      const isPowerOrRCS = role === 'power' || role === 'rcs';

      const queryParams = new URLSearchParams({
        page: targetPage,
        limit: targetLimit
      });
      if (targetSearch && targetSearch.trim()) {
        queryParams.append('search', targetSearch.trim());
      }

      let endpoint = '';
      if (isPowerOrRCS) {
        endpoint = `${API_BASE_URL}/api/societies/all?${queryParams.toString()}`;
      } else {
        queryParams.append('email', user?.email || '');
        queryParams.append('phone', user?.phone || '');
        queryParams.append('name', user?.name || '');
        endpoint = `${API_BASE_URL}/api/societies/inspector?${queryParams.toString()}`;
      }

      const res = await fetch(endpoint);
      const data = await res.json();
      if (data.success && Array.isArray(data.societies)) {
        setSocieties(data.societies);
        setTotalItems(data.totalCount !== undefined ? data.totalCount : data.societies.length);
        setTotalPages(data.totalPages !== undefined ? data.totalPages : (Math.ceil((data.totalCount || data.societies.length) / targetLimit) || 1));
      }
    } catch (err) {
      console.error('Error fetching societies:', err);
    } finally {
      setLoading(false);
    }
  }, [user, page, limit, searchTerm]);

  // Fetch Form Dropdown Meta Options (Divisions, Districts, AR Circles, User Roles)
  const fetchMetaOptions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/societies/meta-options`);
      const data = await res.json();
      if (data.success) {
        setMetaOptions({
          divisions: data.divisions || [],
          districts: data.districts || [],
          arCircles: data.arCircles || [],
          usersByRole: data.usersByRole || { dr: [], ar: [], inspector: [], secretary: [] }
        });
      }
    } catch (err) {
      console.error('Error fetching meta options:', err);
    }
  }, []);

  useEffect(() => {
    fetchSocieties(page, limit, searchTerm);
  }, [page, limit, searchTerm]);

  useEffect(() => {
    fetchMetaOptions();
  }, [fetchMetaOptions]);

  const userRole = (user?.role || '').toLowerCase();
  const isOnlyPower = userRole === 'power';
  const isPower = userRole === 'power' || userRole === 'rcs';

  const [formErrors, setFormErrors] = useState({});

  // Text input state for user typeahead inputs
  const [drSearch, setDrSearch] = useState('');
  const [arSearch, setArSearch] = useState('');
  const [inspectorSearch, setInspectorSearch] = useState('');
  const [secretarySearch, setSecretarySearch] = useState('');

  // Handle Open Create View
  const handleOpenCreateView = () => {
    setEditingSociety(null);
    setFormErrors({});
    setFormData({
      SOCIETY: '',
      DIVISION: '',
      DISTRICT: '',
      AR: '',
      VILLAGE: '',
      selectedDR: '',
      selectedAR: '',
      selectedInspector: '',
      selectedSecretary: ''
    });
    setDrSearch('');
    setArSearch('');
    setInspectorSearch('');
    setSecretarySearch('');
    setViewMode('create');
  };

  // Helper to find existing role user matching society credentials with strict priority
  const findRoleUser = (roleUsers = [], email, phone, name) => {
    if (!roleUsers || roleUsers.length === 0) return null;

    // 1. Prioritize exact email match (case-insensitive) - emails are unique per user
    if (email && email.trim()) {
      const cleanEmail = email.trim().toLowerCase();
      const byEmail = roleUsers.find(u => (u.email || '').trim().toLowerCase() === cleanEmail);
      if (byEmail) return byEmail;
    }

    // 2. Secondary match by phone (skip placeholder numbers like '111111')
    if (phone && String(phone).trim() && String(phone).trim() !== '111111') {
      const cleanPhone = String(phone).trim();
      const byPhone = roleUsers.find(u => String(u.phone || '').trim() === cleanPhone);
      if (byPhone) return byPhone;
    }

    // 3. Fallback to name match only if name is not a generic title like 'AR', 'DR'
    if (name && name.trim()) {
      const cleanName = name.trim().toLowerCase();
      if (cleanName !== 'ar' && cleanName !== 'dr') {
        const byName = roleUsers.find(u => (u.name || '').trim().toLowerCase() === cleanName);
        if (byName) return byName;
      }
    }

    return null;
  };

  // Helper for typeahead input matching
  const lookupUserOption = (roleUsers = [], val = '') => {
    if (!roleUsers || !val || !val.trim()) return null;
    const cleanVal = val.trim();
    const cleanValLower = cleanVal.toLowerCase();

    // 1. Exact match with datalist option string "${name} (${email || phone})" or _id
    const byOption = roleUsers.find(u => `${u.name} (${u.email || u.phone})` === cleanVal || u._id === cleanVal);
    if (byOption) return byOption;

    // 2. Exact email match
    const byEmail = roleUsers.find(u => (u.email || '').trim().toLowerCase() === cleanValLower);
    if (byEmail) return byEmail;

    // 3. Exact phone match (if not placeholder 111111)
    if (cleanVal !== '111111') {
      const byPhone = roleUsers.find(u => String(u.phone || '').trim() === cleanVal);
      if (byPhone) return byPhone;
    }

    // 4. Exact name match (if not generic 'ar' or 'dr')
    if (cleanValLower !== 'ar' && cleanValLower !== 'dr') {
      const byName = roleUsers.find(u => (u.name || '').trim().toLowerCase() === cleanValLower);
      if (byName) return byName;
    }

    return null;
  };

  // Handle Open Edit View
  const handleOpenEditView = (soc) => {
    setEditingSociety(soc);
    setFormErrors({});

    const drUser = findRoleUser(metaOptions.usersByRole.dr, soc.DREMAILID, soc.DRMOBILE, soc.DRNAME);
    const arUser = findRoleUser(metaOptions.usersByRole.ar, soc.AREMAILID, soc.ARMOBILE, soc.ARNAME);
    const inspectorUser = findRoleUser(metaOptions.usersByRole.inspector, soc.INSPECTOREMAILID, soc.INSPECTORMOBILE, soc.INSPECTOR);
    const secretaryUser = findRoleUser(metaOptions.usersByRole.secretary, soc.SECRETARYEMAILID, soc.SECRETARYMOBILE, soc.SECRETARY);

    setFormData({
      SOCIETY: soc.SOCIETY || soc.SocietyName || '',
      DIVISION: soc.DIVISION || '',
      DISTRICT: soc.DISTRICT || '',
      AR: soc.AR || soc.ARNAME || '',
      VILLAGE: soc.VILLAGE || '',
      selectedDR: drUser ? drUser._id : '',
      selectedAR: arUser ? arUser._id : '',
      selectedInspector: inspectorUser ? inspectorUser._id : '',
      selectedSecretary: secretaryUser ? secretaryUser._id : ''
    });

    setDrSearch(drUser ? `${drUser.name} (${drUser.email || drUser.phone})` : (soc.DREMAILID ? `${soc.DRNAME || 'DR'} (${soc.DREMAILID})` : (soc.DRNAME || '')));
    setArSearch(arUser ? `${arUser.name} (${arUser.email || arUser.phone})` : (soc.AREMAILID ? `${soc.ARNAME || 'AR'} (${soc.AREMAILID})` : (soc.ARNAME || '')));
    setInspectorSearch(inspectorUser ? `${inspectorUser.name} (${inspectorUser.email || inspectorUser.phone})` : (soc.INSPECTOREMAILID ? `${soc.INSPECTOR || 'Inspector'} (${soc.INSPECTOREMAILID})` : (soc.INSPECTOR || '')));
    setSecretarySearch(secretaryUser ? `${secretaryUser.name} (${secretaryUser.email || secretaryUser.phone})` : (soc.SECRETARYEMAILID ? `${soc.SECRETARY || 'Secretary'} (${soc.SECRETARYEMAILID})` : (soc.SECRETARY || '')));

    setViewMode('edit');
  };

  // Save Form Handler
  const handleSaveSociety = async () => {
    const errors = {};
    if (!formData.SOCIETY.trim()) errors.SOCIETY = 'Society Name is required';
    if (!formData.DIVISION.trim()) errors.DIVISION = 'Division is required';
    if (!formData.DISTRICT.trim()) errors.DISTRICT = 'District is required';
    if (!formData.AR.trim()) errors.AR = 'AR Circle is required';
    if (!formData.VILLAGE.trim()) errors.VILLAGE = 'Village is required';
    if (!formData.selectedDR) errors.selectedDR = 'DR User is required';
    if (!formData.selectedAR) errors.selectedAR = 'AR User is required';
    if (!formData.selectedInspector) errors.selectedInspector = 'Inspector User is required';
    if (!formData.selectedSecretary) errors.selectedSecretary = 'Secretary User is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    setFormSaving(true);
    try {
      const selectedDRUser = (metaOptions.usersByRole.dr || []).find(u => u._id === formData.selectedDR);
      const selectedARUser = (metaOptions.usersByRole.ar || []).find(u => u._id === formData.selectedAR);
      const selectedInspectorUser = (metaOptions.usersByRole.inspector || []).find(u => u._id === formData.selectedInspector);
      const selectedSecretaryUser = (metaOptions.usersByRole.secretary || []).find(u => u._id === formData.selectedSecretary);

      const payload = {
        SOCIETY: formData.SOCIETY,
        DIVISION: formData.DIVISION,
        DISTRICT: formData.DISTRICT,
        AR: formData.AR,
        VILLAGE: formData.VILLAGE,
        DRNAME: selectedDRUser ? selectedDRUser.name : '',
        DRMOBILE: selectedDRUser ? selectedDRUser.phone : '',
        DREMAILID: selectedDRUser ? selectedDRUser.email : '',
        ARNAME: selectedARUser ? selectedARUser.name : '',
        ARMOBILE: selectedARUser ? selectedARUser.phone : '',
        AREMAILID: selectedARUser ? selectedARUser.email : '',
        INSPECTOR: selectedInspectorUser ? selectedInspectorUser.name : '',
        INSPECTORMOBILE: selectedInspectorUser ? selectedInspectorUser.phone : '',
        INSPECTOREMAILID: selectedInspectorUser ? selectedInspectorUser.email : '',
        SECRETARY: selectedSecretaryUser ? selectedSecretaryUser.name : '',
        SECRETARYMOBILE: selectedSecretaryUser ? selectedSecretaryUser.phone : '',
        SECRETARYEMAILID: selectedSecretaryUser ? selectedSecretaryUser.email : ''
      };

      const url = editingSociety
        ? `${API_BASE_URL}/api/societies/${editingSociety._id}`
        : `${API_BASE_URL}/api/societies`;
      const method = editingSociety ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (data.success) {
        setViewMode('list');
        fetchSocieties();
      } else {
        alert(data.message || 'Failed to save society');
      }
    } catch (err) {
      console.error('Error saving society:', err);
      alert('Error saving society details.');
    } finally {
      setFormSaving(false);
    }
  };

  const handleSearchSubmit = () => {
    setSearchTerm(searchInput.trim());
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const startIndex = (page - 1) * limit;

  // Machine Definitions for CSV export headers
  const MACHINE_CODES = [
    "SUPERSEEDER", "ROTAVATOR", "MULCHER", "RMBPLOUGH", "ZEROTILLDRILL", "ROTARYSLASHER", 
    "PADDYSTRAWCHOPPER", "BALER", "RAKER", "REAPER", "TRACTOR", "HAPPYSEEDER", "SURFACESEEDER",
    "SMARTSEEDER", "SUPERSMS", "SHRUBMASTER", "CUTTERCUMSPREADER", "TRACTORTROLLEY", "LASERLEVELLER",
    "DIRECTSEEDERRICE", "MAIZEPLANTERMECHANICAL", "MAIZEPLANTERVACCUM", "SPRAYPUMP", "PRESSUREDISKS",
    "DISKLOCK", "BIOFERTILIZERSPREADER", "CULTIVATOR", "SUBSOILER", "POWERCLEANER", "DISCRAZOR",
    "HARROWTRIPPLE", "COTTONSEEDDRILL", "WATERTANK", "SUHAGA", "WHEATSEEDFAN", "SEEDGRINDINGMACHINE",
    "JINDRA", "COMBINE", "SEELARCOTTON", "GENERATOR", "WHEATSEEDDRILL", "PLOUGH", "GROUNDLEVELLER",
    "STRAWREAPER", "POTATOPLANTER", "TAWIAN", "SIMPLELEVELLER", "LOADER", "POTATODRILL",
    "MULTIDRILLMACHINE", "PUDDLER", "CHOPPER", "CORNDRILL", "PEASMACHINE", "SEEDDRILL", "CHIZLER",
    "PADDYPLANTER", "DIESELTANK", "BHAUNI", "TOTA", "TILLER", "KARAH", "TARFALI", "RAZOR",
    "THRESHER", "KARCHAMACHINE", "RATOR", "SUGARCANERAGER", "DISCHARROW", "CHAINKUPPI",
    "SUGARCANESEEDER", "GRINDER", "CHANGER", "TRIPPLEPLOUGH", "CORNHARVESTER", "CUTTER",
    "MOTORMACHINE", "DISCPLOUGH", "GAINIPLOUGH", "DUGPLOUGH", "OFFSETHARROW", "JAAL",
    "TARAKADI", "CARROTMACHINE", "DISCBEDMAKER", "LOADERBUCKET", "SEEDGRADER", "ACHATHALL",
    "TAWAWATTA", "JACK"
  ];

  // Export CSV (Fetches all matching records across all pages)
  const handleExportCSV = async () => {
    if (totalItems === 0) return;
    try {
      setExporting(true);
      const role = (user?.role || '').toLowerCase();
      const isPowerOrRCS = role === 'power' || role === 'rcs';

      const queryParams = new URLSearchParams();
      if (searchTerm && searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }

      let endpoint = '';
      if (isPowerOrRCS) {
        endpoint = `${API_BASE_URL}/api/societies/all?${queryParams.toString()}`;
      } else {
        queryParams.append('email', user?.email || '');
        queryParams.append('phone', user?.phone || '');
        queryParams.append('name', user?.name || '');
        endpoint = `${API_BASE_URL}/api/societies/inspector?${queryParams.toString()}`;
      }

      const res = await fetch(endpoint);
      const data = await res.json();
      const exportList = (data.success && Array.isArray(data.societies)) ? data.societies : societies;

      if (!exportList || exportList.length === 0) {
        alert('No societies found to export.');
        return;
      }

      let headers = [];
      if (isPower) {
        headers = [
          "Society Name", "Division", "District", "AR Circle", "Village", 
          "DR Name", "DR Mobile", "DR Email", 
          "AR Name", "AR Mobile", "AR Email", 
          "Inspector Name", "Inspector Mobile", "Inspector Email", 
          "Secretary Name", "Secretary Mobile", "Secretary Email",
          "Acres Covered", "Number of Members",
          "Total Machines", "Total Authorized Machines"
        ];
        MACHINE_CODES.forEach(code => {
          headers.push(`${code} Count`);
          headers.push(`${code} Authorized Count`);
        });
      } else {
        headers = [
          "Society Name", "Division", "District", "AR Circle", "Village", 
          "Secretary Name", "Secretary Mobile", "Secretary Email",
          "Acres Covered", "Number of Members"
        ];
      }
      
      const csvRows = exportList.map(s => {
        const baseRow = [
          `"${s.SOCIETY || s.SocietyName || ''}"`,
          `"${s.DIVISION || ''}"`,
          `"${s.DISTRICT || ''}"`,
          `"${s.AR || s.ARNAME || ''}"`,
          `"${s.VILLAGE || ''}"`
        ];

        if (isPower) {
          baseRow.push(
            `"${s.DRNAME || ''}"`,
            `"${s.DRMOBILE || ''}"`,
            `"${s.DREMAILID || ''}"`,
            `"${s.ARNAME || ''}"`,
            `"${s.ARMOBILE || ''}"`,
            `"${s.AREMAILID || ''}"`,
            `"${s.INSPECTOR || ''}"`,
            `"${s.INSPECTORMOBILE || ''}"`,
            `"${s.INSPECTOREMAILID || ''}"`,
            `"${s.SECRETARY || ''}"`,
            `"${s.SECRETARYMOBILE || ''}"`,
            `"${s.SECRETARYEMAILID || ''}"`
          );

          const mCounts = s.machineCounts || {};
          const aCounts = s.authMachineCounts || {};

          const acresCovered = mCounts.ACRES ?? mCounts.acres ?? s.ACRES ?? 0;
          const noOfMembers = mCounts.NO_OF_MEMBERS ?? mCounts.membersCount ?? s.NO_OF_MEMBERS ?? 0;

          baseRow.push(acresCovered, noOfMembers);

          const totalM = mCounts.TOTALMACHINES !== undefined ? mCounts.TOTALMACHINES : Object.keys(mCounts).reduce((sum, key) => (typeof mCounts[key] === 'number' && key !== '__v' ? sum + mCounts[key] : sum), 0);
          const totalA = Object.keys(aCounts).reduce((sum, key) => (typeof aCounts[key] === 'number' && key !== '__v' ? sum + aCounts[key] : sum), 0);

          baseRow.push(totalM, totalA);

          MACHINE_CODES.forEach(code => {
            baseRow.push(mCounts[code] || 0);
            baseRow.push(aCounts[code] || 0);
          });
        } else {
          const mCounts = s.machineCounts || {};
          const acresCovered = mCounts.ACRES ?? mCounts.acres ?? s.ACRES ?? 0;
          const noOfMembers = mCounts.NO_OF_MEMBERS ?? mCounts.membersCount ?? s.NO_OF_MEMBERS ?? 0;

          baseRow.push(
            `"${s.SECRETARY || ''}"`,
            `"${s.SECRETARYMOBILE || ''}"`,
            `"${s.SECRETARYEMAILID || ''}"`,
            acresCovered,
            noOfMembers
          );
        }

        return baseRow.join(',');
      });

      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...csvRows].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${isPower ? 'Power_Societies_List' : 'Inspector_Assigned_Societies'}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Error exporting CSV data.');
    } finally {
      setExporting(false);
    }
  };

  // Render Form View using FormContainer directly on page
  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <FormContainer
        title={editingSociety ? 'Edit Society Details' : 'Add New Society'}
        onClose={() => setViewMode('list')}
        onSave={handleSaveSociety}
        onCancel={() => setViewMode('list')}
        saving={formSaving}
        saveText={editingSociety ? 'Update Society' : 'Create Society'}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', padding: '10px' }}>
          
          {/* Society Name */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              Society Name *
            </label>
            <input
              type="text"
              placeholder="Enter Society Name"
              value={formData.SOCIETY}
              onChange={(e) => {
                setFormData({ ...formData, SOCIETY: e.target.value });
                if (formErrors.SOCIETY) setFormErrors({ ...formErrors, SOCIETY: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.SOCIETY ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            {formErrors.SOCIETY && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.SOCIETY}</div>}
          </div>

          {/* Division Typeahead */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              Division *
            </label>
            <input
              type="text"
              list="division-options"
              placeholder="Type or Select Division"
              value={formData.DIVISION}
              onChange={(e) => {
                setFormData({ ...formData, DIVISION: e.target.value });
                if (formErrors.DIVISION) setFormErrors({ ...formErrors, DIVISION: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.DIVISION ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            <datalist id="division-options">
              {metaOptions.divisions.map((d, i) => (
                <option key={i} value={d} />
              ))}
            </datalist>
            {formErrors.DIVISION && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.DIVISION}</div>}
          </div>

          {/* District Typeahead */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              District *
            </label>
            <input
              type="text"
              list="district-options"
              placeholder="Type or Select District"
              value={formData.DISTRICT}
              onChange={(e) => {
                setFormData({ ...formData, DISTRICT: e.target.value });
                if (formErrors.DISTRICT) setFormErrors({ ...formErrors, DISTRICT: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.DISTRICT ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            <datalist id="district-options">
              {metaOptions.districts.map((d, i) => (
                <option key={i} value={d} />
              ))}
            </datalist>
            {formErrors.DISTRICT && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.DISTRICT}</div>}
          </div>

          {/* AR Circle Typeahead */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              AR Circle *
            </label>
            <input
              type="text"
              list="arcircle-options"
              placeholder="Type or Select AR Circle"
              value={formData.AR}
              onChange={(e) => {
                setFormData({ ...formData, AR: e.target.value });
                if (formErrors.AR) setFormErrors({ ...formErrors, AR: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.AR ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            <datalist id="arcircle-options">
              {metaOptions.arCircles.map((a, i) => (
                <option key={i} value={a} />
              ))}
            </datalist>
            {formErrors.AR && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.AR}</div>}
          </div>

          {/* Village Text Input */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              Village *
            </label>
            <input
              type="text"
              placeholder="Enter Village"
              value={formData.VILLAGE}
              onChange={(e) => {
                setFormData({ ...formData, VILLAGE: e.target.value });
                if (formErrors.VILLAGE) setFormErrors({ ...formErrors, VILLAGE: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.VILLAGE ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            {formErrors.VILLAGE && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.VILLAGE}</div>}
          </div>

          {/* DR Typeahead */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              DR User *
            </label>
            <input
              type="text"
              list="dr-user-options"
              placeholder="Type or Select DR User"
              value={drSearch}
              onChange={(e) => {
                const val = e.target.value;
                setDrSearch(val);
                const found = lookupUserOption(metaOptions.usersByRole.dr, val);
                setFormData({ ...formData, selectedDR: found ? found._id : '' });
                if (formErrors.selectedDR) setFormErrors({ ...formErrors, selectedDR: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.selectedDR ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            <datalist id="dr-user-options">
              {(metaOptions.usersByRole.dr || []).map((u) => (
                <option key={u._id} value={`${u.name} (${u.email || u.phone})`} />
              ))}
            </datalist>
            {formErrors.selectedDR && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.selectedDR}</div>}
          </div>

          {/* AR Typeahead */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              AR User *
            </label>
            <input
              type="text"
              list="ar-user-options"
              placeholder="Type or Select AR User"
              value={arSearch}
              onChange={(e) => {
                const val = e.target.value;
                setArSearch(val);
                const found = lookupUserOption(metaOptions.usersByRole.ar, val);
                setFormData({ ...formData, selectedAR: found ? found._id : '' });
                if (formErrors.selectedAR) setFormErrors({ ...formErrors, selectedAR: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.selectedAR ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            <datalist id="ar-user-options">
              {(metaOptions.usersByRole.ar || []).map((u) => (
                <option key={u._id} value={`${u.name} (${u.email || u.phone})`} />
              ))}
            </datalist>
            {formErrors.selectedAR && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.selectedAR}</div>}
          </div>

          {/* Inspector Typeahead */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              Inspector User *
            </label>
            <input
              type="text"
              list="inspector-user-options"
              placeholder="Type or Select Inspector User"
              value={inspectorSearch}
              onChange={(e) => {
                const val = e.target.value;
                setInspectorSearch(val);
                const found = lookupUserOption(metaOptions.usersByRole.inspector, val);
                setFormData({ ...formData, selectedInspector: found ? found._id : '' });
                if (formErrors.selectedInspector) setFormErrors({ ...formErrors, selectedInspector: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.selectedInspector ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            <datalist id="inspector-user-options">
              {(metaOptions.usersByRole.inspector || []).map((u) => (
                <option key={u._id} value={`${u.name} (${u.email || u.phone})`} />
              ))}
            </datalist>
            {formErrors.selectedInspector && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.selectedInspector}</div>}
          </div>

          {/* Secretary Typeahead */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              Secretary User *
            </label>
            <input
              type="text"
              list="secretary-user-options"
              placeholder="Type or Select Secretary User"
              value={secretarySearch}
              onChange={(e) => {
                const val = e.target.value;
                setSecretarySearch(val);
                const found = lookupUserOption(metaOptions.usersByRole.secretary, val);
                setFormData({ ...formData, selectedSecretary: found ? found._id : '' });
                if (formErrors.selectedSecretary) setFormErrors({ ...formErrors, selectedSecretary: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.selectedSecretary ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            <datalist id="secretary-user-options">
              {(metaOptions.usersByRole.secretary || []).map((u) => (
                <option key={u._id} value={`${u.name} (${u.email || u.phone})`} />
              ))}
            </datalist>
            {formErrors.selectedSecretary && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.selectedSecretary}</div>}
          </div>

        </div>
      </FormContainer>
    );
  }

  // Filter Bar Slot
  const filterBar = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
        <input
          type="text"
          placeholder={isPower ? "Search by Society, DR, AR, Inspector, Secretary..." : "Search by Society Name, Secretary, District..."}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
          style={{
            padding: '8px 14px',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            fontSize: '13px',
            width: '320px',
            outline: 'none'
          }}
        />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleSearchSubmit}
            style={{
              background: '#f59e0b',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 18px',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleClearFilters}
            style={{
              background: '#f1f5f9',
              color: '#475569',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              padding: '8px 16px',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        {isOnlyPower && (
          <button
            type="button"
            onClick={handleOpenCreateView}
            style={{
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            ➕ Add New Society
          </button>
        )}
        <button
          type="button"
          onClick={handleExportCSV}
          disabled={exporting || loading || totalItems === 0}
          style={{
            background: (exporting || loading || totalItems === 0) ? '#cbd5e1' : '#10b981',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: (!exporting && !loading && totalItems > 0) ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          {exporting ? '⏳ Exporting...' : '📊 Export CSV'}
        </button>
      </div>
    </div>
  );

  const totalCols = isPower ? 11 : 8;

  const paginationFooter = !loading && totalItems > 0 ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
      <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
        Showing {startIndex + 1} to {Math.min(startIndex + limit, totalItems)} of {totalItems} societies
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Per Page Selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Per page:</label>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              outline: 'none',
              background: '#ffffff'
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={250}>250</option>
          </select>
        </div>

        {/* Pagination Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            type="button"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: page === 1 ? '#f1f5f9' : '#ffffff',
              color: page === 1 ? '#94a3b8' : '#1e293b',
              fontSize: '13px',
              fontWeight: '600',
              cursor: page === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>

          <span style={{ fontSize: '13px', color: '#475569', fontWeight: '600', padding: '0 4px' }}>
            Page {page} of {totalPages}
          </span>

          <button
            type="button"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: page >= totalPages ? '#f1f5f9' : '#ffffff',
              color: page >= totalPages ? '#94a3b8' : '#1e293b',
              fontSize: '13px',
              fontWeight: '600',
              cursor: page >= totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <ListContainer
      title="Society List"
      onClose={() => navigate('/home')}
      filterBar={filterBar}
      footer={paginationFooter}
    >
      <div style={{ overflowX: 'auto', maxWidth: '100%', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#ffffff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
              <th style={{ padding: '12px 16px', width: '50px' }}>S.No</th>
              <th style={{ padding: '12px 16px', minWidth: '180px' }}>Society Name</th>
              <th style={{ padding: '12px 16px', minWidth: '120px' }}>Division</th>
              <th style={{ padding: '12px 16px', minWidth: '120px' }}>District</th>
              <th style={{ padding: '12px 16px', minWidth: '130px' }}>AR Circle</th>
              <th style={{ padding: '12px 16px', minWidth: '120px' }}>Village</th>
              {isPower && (
                <>
                  <th style={{ padding: '12px 16px', minWidth: '150px' }}>DR Details</th>
                  <th style={{ padding: '12px 16px', minWidth: '150px' }}>AR Details</th>
                  <th style={{ padding: '12px 16px', minWidth: '150px' }}>Inspector Details</th>
                </>
              )}
              <th style={{ padding: '12px 16px', minWidth: '150px' }}>Secretary Details</th>
              {(isOnlyPower || !isPower) && <th style={{ padding: '12px 16px', width: '220px', textAlign: 'center' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isPower ? (isOnlyPower ? 11 : 10) : 9} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                  Loading Societies...
                </td>
              </tr>
            ) : societies.length === 0 ? (
              <tr>
                <td colSpan={isPower ? (isOnlyPower ? 11 : 10) : 9} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                  No societies found matching criteria.
                </td>
              </tr>
            ) : (
              societies.map((soc, idx) => (
                <tr
                  key={soc._id || idx}
                  style={{
                    borderBottom: '1px solid #f1f5f9',
                    background: idx % 2 === 0 ? '#ffffff' : '#f8fafc'
                  }}
                >
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{startIndex + idx + 1}</td>
                  <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>
                    {soc.SOCIETY || soc.SocietyName}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#475569' }}>{soc.DIVISION || 'N/A'}</td>
                  <td style={{ padding: '12px 16px', color: '#475569' }}>{soc.DISTRICT || 'N/A'}</td>
                  <td style={{ padding: '12px 16px', color: '#475569' }}>{soc.AR || soc.ARNAME || 'N/A'}</td>
                  <td style={{ padding: '12px 16px', color: '#475569' }}>{soc.VILLAGE || 'N/A'}</td>
                  {isPower && (
                    <>
                      <td style={{ padding: '12px 16px', color: '#475569' }}>
                        {soc.DRNAME ? (
                          <div>
                            <div style={{ fontWeight: '500', color: '#1e293b' }}>{soc.DRNAME}</div>
                            {soc.DRMOBILE && <div style={{ fontSize: '12px', color: '#64748b' }}>{soc.DRMOBILE}</div>}
                            {soc.DREMAILID && <div style={{ fontSize: '11px', color: '#3b82f6' }}>{soc.DREMAILID}</div>}
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#475569' }}>
                        {soc.ARNAME ? (
                          <div>
                            <div style={{ fontWeight: '500', color: '#1e293b' }}>{soc.ARNAME}</div>
                            {soc.ARMOBILE && <div style={{ fontSize: '12px', color: '#64748b' }}>{soc.ARMOBILE}</div>}
                            {soc.AREMAILID && <div style={{ fontSize: '11px', color: '#3b82f6' }}>{soc.AREMAILID}</div>}
                          </div>
                        ) : (soc.AR || 'N/A')}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#475569' }}>
                        {soc.INSPECTOR ? (
                          <div>
                            <div style={{ fontWeight: '500', color: '#1e293b' }}>{soc.INSPECTOR}</div>
                            {soc.INSPECTORMOBILE && <div style={{ fontSize: '12px', color: '#64748b' }}>{soc.INSPECTORMOBILE}</div>}
                            {soc.INSPECTOREMAILID && <div style={{ fontSize: '11px', color: '#3b82f6' }}>{soc.INSPECTOREMAILID}</div>}
                          </div>
                        ) : 'N/A'}
                      </td>
                    </>
                  )}
                  <td style={{ padding: '12px 16px', color: '#475569' }}>
                    {soc.SECRETARY ? (
                      <div>
                        <div style={{ fontWeight: '500', color: '#1e293b' }}>{soc.SECRETARY}</div>
                        {soc.SECRETARYMOBILE && <div style={{ fontSize: '12px', color: '#64748b' }}>{soc.SECRETARYMOBILE}</div>}
                        {soc.SECRETARYEMAILID && <div style={{ fontSize: '11px', color: '#3b82f6' }}>{soc.SECRETARYEMAILID}</div>}
                      </div>
                    ) : 'N/A'}
                  </td>
                  {(isOnlyPower || !isPower) && (
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                        {isOnlyPower && (
                          <button
                            type="button"
                            onClick={() => handleOpenEditView(soc)}
                            style={{
                              background: '#f59e0b',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            ✏️ Edit Details
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => navigate('/authorized-machine-counts', { state: { society: soc } })}
                          style={{
                            background: '#3b82f6',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          ⚙️ Authorized Count
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </ListContainer>
  );
};

export default InspectorSocietyListPage;
